import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ClockIcon,
  BoltIcon,
  RectangleStackIcon,
  UsersIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const AIAnalytics = ({ showNotification }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState('7d'); // 7d, 30d, 90d
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/ai/analytics/dashboard?range=${timeRange}`);
      setAnalytics(response.data.data || {});
    } catch (error) {
      logger.error('Error fetching analytics:', error);
      showNotification?.('Failed to load analytics', 'error');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, change, color = 'text-gray-900 dark:text-white' }) => (
    <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <Icon className={`w-5 h-5 ${color}`} />
        {change !== undefined && (
          <span className={`text-xs font-medium ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {change > 0 ? '+' : ''}{change}%
          </span>
        )}
      </div>
      <div className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
        {value || 0}
      </div>
      <div className="text-sm text-gray-500 dark:text-gray-400">
        {label}
      </div>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Squares2X2Icon },
    { id: 'workflows', label: 'Workflows', icon: RectangleStackIcon },
    { id: 'agents', label: 'Agents', icon: UsersIcon }
  ];

  if (loading) {
    return (
      <div className="h-full bg-white dark:bg-[#111111] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white dark:bg-[#111111] overflow-y-auto">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/ai-dashboard')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-[#1a1a1a] rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  AI Analytics
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Business performance metrics and insights
                </p>
              </div>
            </div>

            {/* Time Range Selector */}
            <div className="flex items-center gap-2 p-1 bg-gray-100 dark:bg-[#1a1a1a] rounded-lg">
              {[
                { value: '7d', label: '7 Days' },
                { value: '30d', label: '30 Days' },
                { value: '90d', label: '90 Days' }
              ].map(range => (
                <button
                  key={range.value}
                  onClick={() => setTimeRange(range.value)}
                  className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                    timeRange === range.value
                      ? 'bg-white dark:bg-[#222222] text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex items-center gap-1 border-b border-gray-200 dark:border-gray-800">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-gray-900 dark:border-white text-gray-900 dark:text-white'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={EnvelopeIcon}
                label="Emails Processed"
                value={analytics?.totalEmails || 0}
                color="text-blue-600"
              />
              <StatCard
                icon={CheckCircleIcon}
                label="Successfully Handled"
                value={analytics?.successfulEmails || 0}
                color="text-green-600"
              />
              <StatCard
                icon={ClockIcon}
                label="Avg Response Time"
                value={`${analytics?.avgResponseTime || 0}s`}
                color="text-orange-600"
              />
              <StatCard
                icon={BoltIcon}
                label="Automation Rate"
                value={`${analytics?.automationRate || 0}%`}
                color="text-purple-600"
              />
            </div>

            {/* Workflow Stats */}
            <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Workflow Performance
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="text-3xl font-semibold text-gray-900 dark:text-white mb-1">
                    {analytics?.totalWorkflows || 0}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Total Workflows Executed
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-semibold text-green-600 mb-1">
                    {analytics?.workflowSuccessRate || 0}%
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Success Rate
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="border border-gray-200 dark:border-gray-800 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                  Recent Activity
                </h2>
              </div>
              <div className="px-6 py-4">
                {analytics?.recentActivity && analytics.recentActivity.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.recentActivity.map((activity, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0"
                      >
                        <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-600 mt-2" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-900 dark:text-white">
                            {activity.description}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {new Date(activity.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                    No recent activity
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'workflows' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6">
                <div className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
                  {analytics?.totalWorkflows || 0}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Total Executions
                </div>
              </div>
              <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6">
                <div className="text-2xl font-semibold text-green-600 mb-1">
                  {analytics?.workflowSuccessRate || 0}%
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Success Rate
                </div>
              </div>
              <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6">
                <div className="text-2xl font-semibold text-blue-600 mb-1">
                  {Math.round((analytics?.totalWorkflows || 0) / (timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90)) || 0}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Avg Per Day
                </div>
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                Detailed workflow analytics coming soon
              </p>
            </div>
          </div>
        )}

        {activeTab === 'agents' && (
          <div className="space-y-6">
            <div className="border border-gray-200 dark:border-gray-800 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                  Department Agent Performance
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Activity and success rates by department
                </p>
              </div>
              <div className="px-6 py-4">
                {analytics?.agentPerformance && analytics.agentPerformance.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {analytics.agentPerformance.map((agent, idx) => (
                      <div
                        key={idx}
                        className="p-4 border border-gray-200 dark:border-gray-800 rounded-lg hover:border-gray-300 dark:hover:border-gray-700 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {agent.name}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-[#222222] text-gray-700 dark:text-gray-300">
                            {agent.department}
                          </span>
                        </div>
                        <div className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
                          {agent.emailsHandled}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          emails handled
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 dark:bg-[#222222] rounded-full h-1.5">
                            <div
                              className="bg-green-500 h-1.5 rounded-full"
                              style={{ width: `${agent.successRate}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-gray-900 dark:text-white">
                            {agent.successRate}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
                    No agent activity for this period
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAnalytics;
